import type { EndpointsRegistry, FetchClient } from '~/api/types'
import { ReportServiceEndpoints } from '~/api/endpoints/report-service-endpoints'
import type { ReportServiceApi } from '~/api/services/types'
import type {
  Dashboard,
  DashboardDuplicateInput,
  DashboardInput,
  DashboardSummary,
  DashboardTemplateDuplicateInput,
  DashboardWidget,
  DashboardsQuery,
  DefaultDashboard,
  DefaultDashboardInput,
  LayoutItem,
  WidgetInput
} from '~/types/dashboard'
import type {
  ServiceArtefactLink,
  ServiceCapabilities,
  ServiceCatalog,
  ServiceDefinition,
  ServiceDefinitionInput,
  ServiceDefinitionsQuery,
  ServiceDraftPreviewRequest,
  ServiceDrillRequest,
  ServiceDrillResponse,
  ServiceDuplicateInput,
  ServiceLimits,
  ServicePresetKey,
  ServicePreviewPage,
  ServicePreviewRequest,
  ServiceQueryRequest,
  ServiceQueryResult,
  ServiceRun,
  ServiceRunAccepted,
  ServiceRunFormat,
  ServiceRunRequest,
  ServiceRunsQuery,
  ServiceSchedule,
  ServiceScheduleInput
} from '~/types/report-service'

const JSON_MIME = 'application/json'

const isJsonResponse = (contentType: string | null): boolean => !!contentType && contentType.includes(JSON_MIME)

const toDefinitionQueryParams = (query: ServiceDefinitionsQuery): Record<string, string> => {
  const params: Record<string, string> = {}
  if (query.include) params.include = query.include
  if (query.source) params.source = query.source
  return params
}

const toDashboardQueryParams = (query: DashboardsQuery): Record<string, string> => {
  const params: Record<string, string> = {}
  if (query.include) params.include = query.include
  return params
}

const toQueryParams = (query: ServiceRunsQuery): Record<string, string | number> => {
  const params: Record<string, string | number> = {}
  if (query.definitionId) params.definitionId = query.definitionId
  if (query.presetKey) params.presetKey = query.presetKey
  if (query.limit !== undefined) params.limit = query.limit
  return params
}

export const registerEndpoints = (client: FetchClient, registry: EndpointsRegistry): ReportServiceApi => ({
  getCatalog: async (locale: string): Promise<ServiceCatalog> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.CATALOG_GET, {
      headers: { 'Accept-Language': locale }
    })
    return await client<ServiceCatalog>(url, { method, headers })
  },
  getLimits: async (): Promise<ServiceLimits | null> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.LIMITS_GET)
    return (await client<ServiceLimits | null>(url, { method, headers })) ?? null
  },
  preview: async (
    presetKey: ServicePresetKey,
    request: ServicePreviewRequest,
    locale: string
  ): Promise<ServicePreviewPage> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.PRESET_PREVIEW,
      { params: { key: presetKey }, headers: { 'Accept-Language': locale } },
      { ...request }
    )
    return await client<ServicePreviewPage>(url, { method, headers, body })
  },
  listDefinitions: async (query: ServiceDefinitionsQuery = {}): Promise<ServiceDefinition[]> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.DEFINITIONS_LIST, {
      queryParams: toDefinitionQueryParams(query)
    })
    return (await client<ServiceDefinition[] | null>(url, { method, headers })) ?? []
  },
  createDefinition: async (input: ServiceDefinitionInput): Promise<ServiceDefinition> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DEFINITIONS_CREATE,
      {},
      { ...input }
    )
    return await client<ServiceDefinition>(url, { method, headers, body })
  },
  getDefinition: async (id: string): Promise<ServiceDefinition> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.DEFINITIONS_GET, { params: { id } })
    return await client<ServiceDefinition>(url, { method, headers })
  },
  updateDefinition: async (id: string, input: ServiceDefinitionInput): Promise<ServiceDefinition> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DEFINITIONS_UPDATE,
      { params: { id } },
      { ...input }
    )
    return await client<ServiceDefinition>(url, { method, headers, body })
  },
  deleteDefinition: async (id: string): Promise<void> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.DEFINITIONS_DELETE, {
      params: { id }
    })
    await client(url, { method, headers })
  },
  duplicateFromPreset: async (key: string, input: ServiceDuplicateInput): Promise<ServiceDefinition> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DEFINITIONS_DUPLICATE_FROM_PRESET,
      { params: { key } },
      { ...input }
    )
    return await client<ServiceDefinition>(url, { method, headers, body })
  },
  previewDefinition: async (
    id: string,
    request: ServicePreviewRequest,
    locale: string
  ): Promise<ServicePreviewPage> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DEFINITION_PREVIEW,
      { params: { id }, headers: { 'Accept-Language': locale } },
      { ...request }
    )
    return await client<ServicePreviewPage>(url, { method, headers, body })
  },
  previewDefinitionDraft: async (request: ServiceDraftPreviewRequest, locale: string): Promise<ServicePreviewPage> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DEFINITION_DRAFT_PREVIEW,
      { headers: { 'Accept-Language': locale } },
      { ...request }
    )
    return await client<ServicePreviewPage>(url, { method, headers, body })
  },
  createRun: async (request: ServiceRunRequest): Promise<ServiceRunAccepted> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.RUNS_CREATE,
      {},
      { ...request }
    )
    return await client<ServiceRunAccepted>(url, { method, headers, body })
  },
  listRuns: async (query: ServiceRunsQuery = {}): Promise<ServiceRun[]> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.RUNS_LIST, {
      queryParams: toQueryParams(query)
    })
    return (await client<ServiceRun[] | null>(url, { method, headers })) ?? []
  },
  getRun: async (id: string): Promise<ServiceRun> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.RUNS_GET, { params: { id } })
    return await client<ServiceRun>(url, { method, headers })
  },
  cancelRun: async (id: string): Promise<void> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.RUNS_CANCEL, { params: { id } })
    await client(url, { method, headers })
  },
  artefactLink: async (id: string, format: ServiceRunFormat): Promise<ServiceArtefactLink | null> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.RUNS_ARTEFACT, {
      params: { id, format },
      headers: { Accept: JSON_MIME }
    })
    const response = await client.raw<ServiceArtefactLink | string>(url, { method, headers })
    const data = response._data
    if (!isJsonResponse(response.headers.get('content-type')) || !data || typeof data !== 'object') return null
    return typeof data.url === 'string' ? data : null
  },
  artefactBlob: async (id: string, format: ServiceRunFormat): Promise<Blob> => {
    const { url, method } = registry.createRequest(ReportServiceEndpoints.RUNS_ARTEFACT, { params: { id, format } })
    return await client<Blob>(url, { method, headers: { Accept: '*/*' }, responseType: 'blob', redirect: 'follow' })
  },
  listSchedules: async (): Promise<ServiceSchedule[]> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.SCHEDULES_LIST)
    return (await client<ServiceSchedule[] | null>(url, { method, headers })) ?? []
  },
  createSchedule: async (input: ServiceScheduleInput): Promise<ServiceSchedule> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.SCHEDULES_CREATE,
      {},
      { ...input }
    )
    return await client<ServiceSchedule>(url, { method, headers, body })
  },
  getSchedule: async (id: string): Promise<ServiceSchedule> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.SCHEDULES_GET, { params: { id } })
    return await client<ServiceSchedule>(url, { method, headers })
  },
  updateSchedule: async (id: string, input: ServiceScheduleInput): Promise<ServiceSchedule> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.SCHEDULES_UPDATE,
      { params: { id } },
      { ...input }
    )
    return await client<ServiceSchedule>(url, { method, headers, body })
  },
  deleteSchedule: async (id: string): Promise<void> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.SCHEDULES_DELETE, {
      params: { id }
    })
    await client(url, { method, headers })
  },
  runScheduleNow: async (id: string): Promise<ServiceRunAccepted> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.SCHEDULES_RUN_NOW, {
      params: { id }
    })
    return await client<ServiceRunAccepted>(url, { method, headers })
  },
  query: async (request: ServiceQueryRequest, locale: string): Promise<ServiceQueryResult> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.QUERY,
      { headers: { 'Accept-Language': locale } },
      { ...request }
    )
    return await client<ServiceQueryResult>(url, { method, headers, body })
  },
  drill: async (request: ServiceDrillRequest, locale: string): Promise<ServiceDrillResponse> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DRILL,
      { headers: { 'Accept-Language': locale } },
      { ...request }
    )
    return await client<ServiceDrillResponse>(url, { method, headers, body })
  },
  getCapabilities: async (): Promise<ServiceCapabilities> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.CAPABILITIES_GET)
    return await client<ServiceCapabilities>(url, { method, headers })
  },
  listDashboards: async (query: DashboardsQuery, locale: string): Promise<DashboardSummary[]> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.DASHBOARDS_LIST, {
      queryParams: toDashboardQueryParams(query),
      headers: { 'Accept-Language': locale }
    })
    return (await client<DashboardSummary[] | null>(url, { method, headers })) ?? []
  },
  createDashboard: async (input: DashboardInput): Promise<Dashboard> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DASHBOARDS_CREATE,
      {},
      { ...input }
    )
    return await client<Dashboard>(url, { method, headers, body })
  },
  getDashboard: async (id: string, locale: string): Promise<Dashboard> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.DASHBOARDS_GET, {
      params: { id },
      headers: { 'Accept-Language': locale }
    })
    return await client<Dashboard>(url, { method, headers })
  },
  updateDashboard: async (id: string, input: DashboardInput): Promise<Dashboard> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DASHBOARDS_UPDATE,
      { params: { id } },
      { ...input }
    )
    return await client<Dashboard>(url, { method, headers, body })
  },
  deleteDashboard: async (id: string): Promise<void> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.DASHBOARDS_DELETE, {
      params: { id }
    })
    await client(url, { method, headers })
  },
  updateDashboardLayout: async (id: string, layout: LayoutItem[]): Promise<void> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.DASHBOARDS_LAYOUT_UPDATE, {
      params: { id }
    })
    await client(url, { method, headers, body: JSON.stringify(layout) })
  },
  duplicateDashboard: async (id: string, input: DashboardDuplicateInput, locale: string): Promise<Dashboard> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DASHBOARDS_DUPLICATE,
      { params: { id }, headers: { 'Accept-Language': locale } },
      { ...input }
    )
    return await client<Dashboard>(url, { method, headers, body })
  },
  duplicateDashboardFromTemplate: async (
    key: string,
    input: DashboardTemplateDuplicateInput,
    locale: string
  ): Promise<Dashboard> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DASHBOARDS_DUPLICATE_FROM_TEMPLATE,
      { params: { key }, headers: { 'Accept-Language': locale } },
      { ...input }
    )
    return await client<Dashboard>(url, { method, headers, body })
  },
  createWidget: async (dashboardId: string, input: WidgetInput): Promise<DashboardWidget> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DASHBOARD_WIDGETS_CREATE,
      { params: { id: dashboardId } },
      { ...input }
    )
    return await client<DashboardWidget>(url, { method, headers, body })
  },
  updateWidget: async (dashboardId: string, widgetId: string, input: WidgetInput): Promise<DashboardWidget> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DASHBOARD_WIDGETS_UPDATE,
      { params: { id: dashboardId, widgetId } },
      { ...input }
    )
    return await client<DashboardWidget>(url, { method, headers, body })
  },
  deleteWidget: async (dashboardId: string, widgetId: string): Promise<void> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.DASHBOARD_WIDGETS_DELETE, {
      params: { id: dashboardId, widgetId }
    })
    await client(url, { method, headers })
  },
  getDefaultDashboard: async (): Promise<DefaultDashboard> => {
    const { url, method, headers } = registry.createRequest(ReportServiceEndpoints.DASHBOARDS_DEFAULT_GET)
    return await client<DefaultDashboard>(url, { method, headers })
  },
  setDefaultDashboard: async (input: DefaultDashboardInput): Promise<void> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DASHBOARDS_DEFAULT_SET,
      {},
      { ...input }
    )
    await client(url, { method, headers, body })
  },
  setLicenceDefaultDashboard: async (input: DefaultDashboardInput): Promise<void> => {
    const { url, method, headers, body } = registry.createRequest(
      ReportServiceEndpoints.DASHBOARDS_LICENCE_DEFAULT_SET,
      {},
      { ...input }
    )
    await client(url, { method, headers, body })
  }
})
